import { useCart } from '@/context/CartContext';

interface CartSidebarProps {
  open: boolean;
  onClose: () => void;
  onCheckout: () => void;
}

export default function CartSidebar({ open, onClose, onCheckout }: CartSidebarProps) {
  const { items, removeFromCart, updateQuantity, totalItems, totalPrice } = useCart();

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      )}

      <div
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-96 bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div className="flex items-center gap-2">
            <i className="ri-shopping-cart-2-line text-xl text-primary-500"></i>
            <h2 className="font-heading text-lg font-semibold text-foreground-800">Carrito</h2>
            <span className="bg-primary-100 text-primary-700 text-xs font-semibold px-2 py-0.5 rounded-full">
              {totalItems}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-foreground-400">
              <i className="ri-shopping-cart-line text-5xl mb-3"></i>
              <p className="text-sm">El carrito está vacío</p>
              <p className="text-xs mt-1">Agrega productos desde el catálogo</p>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="flex gap-3 p-3 rounded-lg border border-background-200 bg-background-50"
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-white border border-background-200">
                  <img
                    src={item.imagen}
                    alt={item.nombre}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-semibold text-foreground-800 line-clamp-2 leading-tight">
                    {item.nombre}
                  </h4>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    Bs. {item.precio.toFixed(2)} c/u
                  </p>

                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center border border-background-300 rounded-md">
                      <button
                        onClick={() => updateQuantity(item.id, item.cantidad - 1)}
                        className="w-7 h-7 flex items-center justify-center text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer rounded-l-md"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <span className="w-8 text-center text-xs font-medium text-foreground-800">
                        {item.cantidad}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.cantidad + 1)}
                        disabled={item.cantidad >= item.stock}
                        className="w-7 h-7 flex items-center justify-center text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer rounded-r-md disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>

                    <span className="text-xs font-semibold text-foreground-700 ml-auto">
                      Bs. {(item.precio * item.cantidad).toFixed(2)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => removeFromCart(item.id)}
                  className="p-1.5 rounded-lg text-foreground-400 hover:text-accent-500 hover:bg-accent-50 transition-colors cursor-pointer self-start flex-shrink-0"
                >
                  <i className="ri-delete-bin-line text-base"></i>
                </button>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-background-200 px-5 py-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground-500">Total productos:</span>
              <span className="font-medium text-foreground-700">{totalItems}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground-500 text-sm">Total a pagar:</span>
              <span className="text-xl font-bold text-primary-600">
                Bs. {totalPrice.toFixed(2)}
              </span>
            </div>
            <button
              onClick={onCheckout}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-double-line"></i>
              <span>Finalizar Venta</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
}